/**
 * Relationships Home Page
 *
 * Landing page for /prioritization/relationships.
 * Explains canonical relationship types and named failure patterns.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, ArrowRight, AlertTriangle, Info } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import {
  RELATIONSHIP_UI_GUIDE,
  RELATIONSHIP_TYPE_COUNT,
  RELATIONSHIP_TYPE_ROUTES,
  relationshipGuideCardClass,
} from '../../lib/relationshipGuide';
import {
  ANTIPATTERN_GROUPS,
  PRIORITIZATION_ANTIPATTERNS,
  antipatternCardClass,
  dispositionBadgeClass,
  dispositionLabel,
} from '../../lib/prioritizationAntipatterns';
import { RelationshipReferenceModal, type RelationshipReferenceTab } from '../../components/prioritization/relationships/RelationshipReferenceModal';
import { useTheme } from '../../contexts/ThemeContext';

export default function RelationshipsHomePage() {
  const { isDark } = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<RelationshipReferenceTab>('types');

  const openModal = (tab: RelationshipReferenceTab) => {
    setModalTab(tab);
    setModalOpen(true);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/15"
        title="Relationships"
        subtitle={`${RELATIONSHIP_TYPE_COUNT} canonical ways items relate — and the structural failure patterns that invalidate ranking when they combine wrong.`}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openModal('types')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  border transition-colors ${
                isDark
                  ? 'border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--bg-raised)]/5'
                  : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
            >
              <Info className="h-3.5 w-3.5" />
              Types guide
            </button>
            <button
              type="button"
              onClick={() => openModal('patterns')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  border transition-colors ${
                isDark
                  ? 'border-[var(--mustard)]/50 text-[var(--mustard)] hover:bg-[var(--mustard)]/30'
                  : 'border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)]'
              }`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Failure patterns
            </button>
          </div>
        }
      />

      <div className={` border px-5 py-4 text-sm ${
        isDark
          ? 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-secondary)]'
          : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-sec)]'
      }`}>
        Relationships are directional or symmetric structural annotations between items.
        They influence grouping, sequencing, and merge decisions — but they do not change
        an item's lifecycle on their own. When relationships combine badly inside a pool
        or track, the platform surfaces named failure patterns before a session runs.
      </div>

      <section className="space-y-4">
        <h2 className={`text-base font-semibold ${isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-pri)]'}`}>
          Relationship types
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {RELATIONSHIP_UI_GUIDE.map((entry) => {
            const { Icon } = entry;
            const route = RELATIONSHIP_TYPE_ROUTES[entry.id];
            const iconCls = isDark ? entry.iconColorDark : entry.iconColor;

            return (
              <div key={entry.id} className={`flex flex-col gap-3 ${relationshipGuideCardClass(isDark, entry)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 shrink-0 ${iconCls}`} />
                    <div>
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-pri)]'}`}>
                        {entry.label}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium border ${
                          isDark ? 'bg-[var(--bg-raised)]/5 border-white/10 text-[var(--text-ter)]' : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)]'
                        }`}>
                          {entry.axis}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>·</span>
                        <span className={`text-xs ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                          Bond: {entry.bond}
                        </span>
                      </div>
                    </div>
                  </div>
                  {route && (
                    <Link
                      to={route}
                      className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5  transition-colors ${
                        isDark
                          ? 'bg-[var(--bg-raised)]/5 hover:bg-[var(--bg-raised)]/10 text-[var(--text-ter)] border border-[var(--rule)]'
                          : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)] border border-[var(--rule)]'
                      }`}
                    >
                      View all
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>

                <p className={`text-sm font-medium italic ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                  "{entry.tagline}"
                </p>

                <p className={`text-sm leading-relaxed ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                  {entry.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className={`text-base font-semibold ${isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-pri)]'}`}>
              Failure patterns
            </h2>
            <p className={`text-sm mt-1 max-w-2xl ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
              Named structural mistakes — detectable on pools and tracks (A–B), catalogued for planning (C) and governance (D).
              Live detection also appears on the Relationship Graph when Pool Violations is enabled.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openModal('patterns')}
            className={`text-xs font-medium underline-offset-2 hover:underline ${isDark ? 'text-[var(--mustard)]' : 'text-[var(--mustard)]'}`}
          >
            Open full catalog
          </button>
        </div>

        {ANTIPATTERN_GROUPS.map((group) => {
          const entries = PRIORITIZATION_ANTIPATTERNS.filter((e) => e.group === group.id);
          return (
            <div key={group.id} className="space-y-3">
              <h3 className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                {group.id}. {group.title}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {entries.map((entry) => (
                  <div key={entry.id} className={antipatternCardClass(isDark, entry.group)}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <span className={`text-xs font-mono mr-2 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{entry.code}</span>
                        <span className={`text-sm font-semibold ${isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>{entry.name}</span>
                        <p className={`text-xs italic mt-0.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>aka {entry.aka}</p>
                      </div>
                      <span className={dispositionBadgeClass(entry.disposition, isDark)}>{dispositionLabel(entry.disposition)}</span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>{entry.pain}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <RelationshipReferenceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        isDark={isDark}
        initialTab={modalTab}
      />
    </div>
  );
}
